import { useState, useEffect } from 'react';
import axios from 'axios';
import { Flame, Trophy, Star, Medal, Zap, CheckCircle, Clock } from 'lucide-react';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function AchievementBadges({ childId }) {
  const [profile, setProfile] = useState(null);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const [profileRes, badgesRes] = await Promise.all([
          axios.get(`${API}/gamification/profile/${childId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
          axios.get(`${API}/badges/${childId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
        ]);
        setProfile(profileRes.data);
        setBadges(badgesRes.data?.badges || badgesRes.data || []);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetch();
  }, [childId]);

  if (loading) return <div className="animate-pulse h-32 bg-zinc-900/50 rounded-xl" />;
  if (!profile && !badges.length) return null;

  const levelColours = {
    'Rookie': '#9CA3AF', 'Bronze': '#CD7F32', 'Silver': '#C0C0C0',
    'Gold': '#FFD700', 'Platinum': '#E5E4E2', 'Diamond': '#B9F2FF', 'Elite': '#FF025F'
  };

  const level = profile?.level || {};
  const levelColour = levelColours[level.name] || '#9CA3AF';
  const earned = badges.filter(b => b.earned || b.awarded);
  const streak = profile?.streak?.current || 0;

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="achievement-badges">
      {/* Level & Points Header */}
      <div className="p-5 flex items-center gap-4">
        <div 
          className="w-14 h-14 rounded-xl flex items-center justify-center border-2"
          style={{ borderColor: levelColour, backgroundColor: `${levelColour}15` }}
        >
          <Trophy className="h-7 w-7" style={{ color: levelColour }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: levelColour }}>
              {level.name || 'Rookie'}
            </h3>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400">
              Lvl {level.level || 1}
            </span>
          </div>
          <p className="text-xs text-zinc-400 mt-0.5">
            {profile?.total_points || 0} points
            {profile?.next_level && ` • ${profile.next_level.min_points - (profile?.total_points || 0)} to ${profile.next_level.name}`}
          </p>
          {/* XP Bar */}
          {profile?.next_level && (
            <div className="mt-2 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all"
                style={{ 
                  width: `${Math.min(100, ((profile?.total_points || 0) - (level.min_points || 0)) / ((profile?.next_level?.min_points || 1) - (level.min_points || 0)) * 100)}%`,
                  backgroundColor: levelColour 
                }}
              />
            </div>
          )}
        </div>
        {/* Streak */}
        {streak > 0 && (
          <div className="text-center px-3 py-2 bg-orange-500/10 border border-orange-500/30 rounded-lg">
            <Flame className="h-5 w-5 text-orange-400 mx-auto" />
            <p className="text-lg font-bold text-orange-400">{streak}</p>
            <p className="text-[9px] text-orange-400/70 uppercase tracking-wider">Streak</p>
          </div>
        )}
      </div>

      {/* Badges Grid */}
      {earned.length > 0 && (
        <div className="px-5 pb-5">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-3">
            Badges Earned ({earned.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {earned.slice(0, 12).map((badge, i) => {
              const tierColour = badge.tier === 'gold' ? '#FFD700' : badge.tier === 'silver' ? '#C0C0C0' : badge.tier === 'diamond' ? '#B9F2FF' : '#CD7F32';
              return (
                <div 
                  key={i}
                  className="group relative w-12 h-12 rounded-lg border flex items-center justify-center transition-transform hover:scale-110 cursor-pointer"
                  style={{ borderColor: tierColour, backgroundColor: `${tierColour}15` }}
                  title={`${badge.name}: ${badge.description}`}
                >
                  <Star className="h-5 w-5" style={{ color: tierColour }} />
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                    <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 shadow-xl whitespace-nowrap">
                      <p className="text-xs font-semibold text-white">{badge.name}</p>
                      <p className="text-[10px] text-zinc-400">{badge.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {profile?.recent_activity?.length > 0 && (
        <div className="px-5 pb-5 border-t border-zinc-800 pt-4">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold mb-2">Recent Activity</p>
          <div className="space-y-1.5">
            {profile.recent_activity.slice(0, 5).map((activity, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <Zap className="h-3 w-3 text-[#02b5ff]" />
                <span className="text-zinc-300 flex-1">{activity.description}</span>
                <span className="text-[#02b5ff] font-semibold">+{activity.points}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
